/**
 * Classification of a Google Places result for Colorado regulated marijuana
 * delivery under **1 CCR 212-3 Rule 3-615(B)** (private residence only).
 *
 * Takes the **`types`** array that the Google Places API returns for a
 * `place_id` and decides whether the address can receive a delivery.
 * Since ADR-129 the API client calls Places API (New), whose `types`
 * carry the Table A lodging sub-types (`hotel`, `cottage`, …) that let
 * this classifier tell a hotel from a rented residence. The Geocoding
 * API still returns only the broad `lodging` type; see the ingest
 * verifier for how that is refined.
 *
 * **Not authoritative** — driver ops still enforce compliance at the door.
 * This is a second layer on top of the free-text regex check so we reject
 * establishments, schools, lodging, etc. that the regex cannot catch.
 */
export const GOOGLE_PLACES_RESIDENCE_REJECT_REASONS = [
    "school",
    "university",
    "hospital",
    "lodging",
    "post_office",
    "business",
    "transit_or_airport",
    "other_establishment",
    /**
     * The Google Places check couldn't run — either `GOOGLE_MAPS_API_KEY`
     * isn't configured on the API process, or the Place Details / Nearby
     * Search call failed. Used to fail the compliance gate **closed** in
     * those cases. Pre-fix the same scenarios silently allowed-with-
     * warning, which let schools / hospitals through whenever the API key
     * was missing on staging.
     */
    "verification_unavailable",
];
/**
 * Lodging sub-types from Places API (New) Table A that are commercial
 * lodging: a hotel, a motel, a hostel, a campground. Refused as `lodging`
 * on every path (COMP-007; HB25-1331 not treated as effective).
 */
export const LODGING_REFUSED_SUBTYPES = [
    "hotel",
    "motel",
    "resort_hotel",
    "extended_stay_hotel",
    "hostel",
    "inn",
    "bed_and_breakfast",
    "campground",
    "rv_park",
    "camping_cabin",
    "japanese_inn",
    "budget_japanese_inn",
];
/**
 * Lodging sub-types that describe a dwelling someone rents whole or in
 * part: a short-term rental (Airbnb and the like), a cottage, a guest
 * house, a private room in a home, a mobile-home park. Teri decided on
 * 2026-10-09 that short-term rentals are private residences and are
 * allowed, while hotels and motels stay refused (ADR-129). These are the
 * only lodging types that pass; a place tagged only with the broad
 * `lodging` type and none of these fails closed as `lodging`, because
 * without a sub-type a hotel and a rental are indistinguishable.
 */
export const LODGING_RESIDENCE_SUBTYPES = [
    "cottage",
    "guest_house",
    "private_guest_room",
    "farmstay",
    "mobile_home_park",
];
/**
 * Places API (New) Table A "Housing" types: a building or complex whose
 * units are dwellings. Google often tags an apartment high-rise with
 * the broad `lodging` type as well (1600 Glenarm Pl, Denver, refused on
 * staging 2026-10-13 as a hotel), so these count as a residence even
 * when `lodging` is present (ADR-129 amendment, 2026-10-13). A hotel
 * sub-type alongside still wins; a specific commercial type alongside
 * is `business`.
 */
export const HOUSING_RESIDENCE_TYPES = [
    "apartment_building",
    "apartment_complex",
    "condominium_complex",
    "housing_complex",
];
/**
 * USPS secondary-unit designators that mark a dwelling inside a larger
 * building: `Apt 1203`, `Unit 4B`, `#12`, `Bldg C Apt 2`, a penthouse,
 * a trailer / lot / space number in a park, a floor. `Suite` and `Room`
 * are left out on purpose: a suite is office vocabulary and a room is
 * hotel vocabulary, and this signal only matters when Google has typed
 * a place as bare `lodging` without saying whether it is a hotel.
 */
const UNIT_DESIGNATOR_WORD = /(?:^|[\s,.])(?:apt|apartment|unit|bldg|building|fl|floor|lot|spc|space|trlr|trailer|ph|penthouse)\b\.?\s*#?\s*[a-z0-9]{1,6}(?:-[a-z0-9]{1,4})?\b/i;
const UNIT_DESIGNATOR_HASH = /(?:^|\s)#\s*[a-z0-9]{1,6}(?:-[a-z0-9]{1,4})?\b/i;
/** A line 2 that is only a short token with a digit in it: `1203`, `4B`, `B-12`. */
const UNIT_BARE_TOKEN = /^(?:[a-z]?\d{1,5}[a-z]?|[a-z]-?\d{1,5})(?:-\d{1,4}[a-z]?)?$/i;
/**
 * Whether the submitted address names a unit inside a building. Looks
 * at line 2 first (where partners put `Apt 1203`), then at line 1 for an
 * inline designator (`1600 Glenarm Pl Apt 1203`). A line 2 that is just
 * a number or a short alphanumeric token counts as a unit; a line 2 with
 * words and no designator (a building name, delivery notes) does not.
 */
export function addressHasUnitDesignator(input) {
    const line2 = (input.addressLine2 ?? "").trim();
    if (line2.length > 0) {
        if (UNIT_BARE_TOKEN.test(line2))
            return true;
        if (UNIT_DESIGNATOR_WORD.test(line2) || UNIT_DESIGNATOR_HASH.test(line2))
            return true;
    }
    const line1 = input.addressLine1.trim();
    return UNIT_DESIGNATOR_WORD.test(line1) || UNIT_DESIGNATOR_HASH.test(line1);
}
/** Place types that definitively disqualify (hard reject). */
const HARD_REJECT_TYPE_TO_REASON = [
    ["school", "school"],
    ["primary_school", "school"],
    ["secondary_school", "school"],
    ["university", "university"],
    ["hospital", "hospital"],
    ...LODGING_REFUSED_SUBTYPES.map((t) => [t, "lodging"]),
    ["post_office", "post_office"],
    ["airport", "transit_or_airport"],
    ["bus_station", "transit_or_airport"],
    ["train_station", "transit_or_airport"],
    ["subway_station", "transit_or_airport"],
    ["transit_station", "transit_or_airport"],
    ["light_rail_station", "transit_or_airport"],
];
/**
 * Types we accept as plausibly residential (or at least not clearly commercial).
 * `premise`, `subpremise`, `street_address` are what Google usually returns for
 * a house or apartment; `route` + `geocode` alone (no establishment) is also OK.
 */
const RESIDENTIAL_TYPES = new Set([
    "premise",
    "subpremise",
    "street_address",
    "route",
    "geocode",
    "postal_code",
    "neighborhood",
]);
/**
 * The two tags Google puts on every listed place of any kind. Carry no
 * information about *what* the place is; only meaningful when no
 * specific type accompanies them.
 */
const GENERIC_ESTABLISHMENT_TAGS = new Set(["establishment", "point_of_interest"]);
/** Broad establishment types — reject unless also a clear residential type. */
const ESTABLISHMENT_TYPES = new Set([
    ...GENERIC_ESTABLISHMENT_TAGS,
    "store",
    "shopping_mall",
    "restaurant",
    "bar",
    "cafe",
    "bank",
    "gas_station",
    "gym",
    "pharmacy",
    "movie_theater",
    "night_club",
    "park",
    "stadium",
    "tourist_attraction",
    "church",
    "place_of_worship",
    "cemetery",
    "funeral_home",
    "courthouse",
    "embassy",
    "library",
    "museum",
    "police",
    "fire_station",
    "city_hall",
    "local_government_office",
]);
/**
 * Classify a Google Places result by inspecting its **`types`** array.
 * Returns `{ ok: true }` when the address appears residential; otherwise
 * `{ ok: false, reason }` with the narrowest matching rejection reason.
 *
 * Unknown / empty type arrays return `{ ok: true }` — callers treat
 * "unknown" the same as "no Google signal" and fall back to the regex check.
 */
export function classifyGooglePlaceForColoradoDelivery(input) {
    const set = new Set(input.types.map((t) => t.toLowerCase()));
    for (const [type, reason] of HARD_REJECT_TYPE_TO_REASON) {
        if (set.has(type)) {
            return { ok: false, reason };
        }
    }
    // Lodging that is not a hotel (none of LODGING_REFUSED_SUBTYPES matched
    // above), and housing. A rental sub-type or a housing type makes the
    // place a dwelling; every listed place also carries `establishment` /
    // `point_of_interest`, so those generic tags must not push it into the
    // establishment branch below. A specific commercial type alongside (a
    // guest house that is also a restaurant) is still a commercial unit,
    // refused as `business`.
    //
    // Bare `lodging` with no sub-type either way is what the legacy Places
    // API and the Geocoding API return for every kind of lodging, and what
    // the New API returns for a place Google has not sub-typed; many
    // apartment buildings carry it. It is a residence when the address
    // names a unit in the building (`subpremise` from Google, or a unit
    // designator in the submitted text); without one it fails closed as
    // `lodging`, because a hotel and an apartment block are then
    // indistinguishable.
    const residenceSubtype = LODGING_RESIDENCE_SUBTYPES.some((t) => set.has(t)) || HOUSING_RESIDENCE_TYPES.some((t) => set.has(t));
    const bareLodgingWithUnit = set.has("lodging") && (set.has("subpremise") || input.unitDesignator === true);
    if (residenceSubtype || bareLodgingWithUnit) {
        const specificCommercial = [...ESTABLISHMENT_TYPES].some((t) => !GENERIC_ESTABLISHMENT_TAGS.has(t) && set.has(t));
        return specificCommercial ? { ok: false, reason: "business" } : { ok: true };
    }
    if (set.has("lodging")) {
        return { ok: false, reason: "lodging" };
    }
    const hasResidential = [...RESIDENTIAL_TYPES].some((t) => set.has(t));
    const hasEstablishment = [...ESTABLISHMENT_TYPES].some((t) => set.has(t));
    if (hasEstablishment && !hasResidential) {
        return { ok: false, reason: "other_establishment" };
    }
    if (hasEstablishment && hasResidential) {
        // Ambiguous: building that contains both a premise and a business (e.g., a
        // live/work loft or a home-based storefront). Err on the side of rejecting
        // because regulated delivery cannot go to a commercial unit.
        return { ok: false, reason: "business" };
    }
    return { ok: true };
}
//# sourceMappingURL=google-places-classification.js.map